export type ToastVariant = 'success' | 'info' | 'warning' | 'error';

export interface ToastItem {
  id: number;
  variant: ToastVariant;
  message: string;
  // PROTOTYPE: render with the DESIGN.md toast style (black, 8px radius).
  ds?: boolean;
}

export interface ToastOptions {
  ds?: boolean;
}

type Listener = () => void;

interface ToastTimer {
  handle: ReturnType<typeof setTimeout> | undefined;
  remaining: number;
  startedAt: number;
}

const AUTO_DISMISS_MS: Record<ToastVariant, number | null> = {
  success: 6000,
  info: 8000,
  warning: null,
  error: null,
};

const EMPTY_TOASTS: ToastItem[] = [];

let toasts: ToastItem[] = [];
let nextId = 1;
const listeners = new Set<Listener>();
const timers = new Map<number, ToastTimer>();

const emit = () => {
  listeners.forEach(listener => listener());
};

export const subscribeToasts = (listener: Listener): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

export const getToastsSnapshot = (): ToastItem[] => toasts;

export const getServerToastsSnapshot = (): ToastItem[] => EMPTY_TOASTS;

export const dismissToast = (id: number): void => {
  const timer = timers.get(id);
  if (timer?.handle) clearTimeout(timer.handle);
  timers.delete(id);
  toasts = toasts.filter(toast => toast.id !== id);
  emit();
};

const startTimer = (id: number, timer: ToastTimer): void => {
  timer.startedAt = Date.now();
  timer.handle = setTimeout(() => dismissToast(id), timer.remaining);
};

export const pauseToast = (id: number): void => {
  const timer = timers.get(id);
  if (!timer?.handle) return;
  clearTimeout(timer.handle);
  timer.handle = undefined;
  timer.remaining = Math.max(timer.remaining - (Date.now() - timer.startedAt), 1000);
};

export const resumeToast = (id: number): void => {
  const timer = timers.get(id);
  if (!timer || timer.handle) return;
  startTimer(id, timer);
};

const showToast = (
  variant: ToastVariant,
  message: string,
  autoDismissMs?: number,
  options?: ToastOptions
): number => {
  const id = nextId++;
  toasts = [...toasts, { id, variant, message, ds: options?.ds }];
  const duration = autoDismissMs ?? AUTO_DISMISS_MS[variant];
  if (duration !== null) {
    const timer: ToastTimer = { handle: undefined, remaining: duration, startedAt: 0 };
    timers.set(id, timer);
    startTimer(id, timer);
  }
  emit();
  return id;
};

export const toast = {
  success: (message: string, options?: ToastOptions): number =>
    showToast('success', message, undefined, options),
  info: (message: string, options?: ToastOptions): number =>
    showToast('info', message, undefined, options),
  warning: (
    message: string,
    autoDismissMs?: number,
    options?: ToastOptions
  ): number => showToast('warning', message, autoDismissMs, options),
  error: (
    message: string,
    autoDismissMs?: number,
    options?: ToastOptions
  ): number => showToast('error', message, autoDismissMs, options),
};
